import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import API from "./config/API";

function ReadyToJoinUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // USER TYPE FILTER
  const [userType, setUserType] = useState("ALL");

  // PAYMENT FILTER
  const [paymentFilter, setPaymentFilter] = useState("ALL");

  const user = JSON.parse(localStorage.getItem("user"));
  const pgOwnerEmailId = user?.emailId;

  // ==========================================================
  // FETCH USERS
  // ==========================================================

  useEffect(() => {
    if (pgOwnerEmailId) {
      fetchUsers();
    }
  }, [pgOwnerEmailId, userType]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API.V2}/pgOwner/getReadyJoinAndVacateUsers`,
        {
          params: {
            emailId: pgOwnerEmailId,
            userType: userType, // ALL_USERS / READY_TO_JOIN / READY_TO_VACATE
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      console.log("User Type:", userType);
      console.log("API Response:", response.data);

      if (response.data.statusCode === 200) {
        const data = Array.isArray(response.data.data)
          ? response.data.data.filter((item) => item != null)
          : [];

        const formattedUsers = data.map((item) => ({
          ...item,

          userTypeFilter:
            item.readyToJoinVacateStatus?.toUpperCase() === "READY_TO_JOIN"
              ? "READY_TO_JOIN"
              : "READY_TO_VACATE",
        }));
        setUsers(formattedUsers);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };
  // ==========================================================
  // PAYMENT FILTER
  // ==========================================================

  const filteredUsers = useMemo(() => {
    if (paymentFilter === "ALL") {
      return users;
    }

    return users.filter((item) => {
      const status = item.paymentStatus?.toUpperCase();

      return status === paymentFilter.toUpperCase();
    });
  }, [users, paymentFilter]);

  // ==========================================================
  // DATE FORMAT
  // ==========================================================

  const formatDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);

    if (isNaN(d.getTime())) {
      return date;
    }

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ==========================================================
  // PAYMENT STATUS STYLE
  // ==========================================================

  const getPaymentStatusStyle = (status) => {
    const paymentStatus = status?.toUpperCase();

    if (paymentStatus === "SUCCESS" || paymentStatus === "PAID") {
      return {
        background: "#dcfce7",
        color: "#15803d",
      };
    }

    if (paymentStatus === "PARTIALLY_PAID") {
      return {
        background: "#fef3c7",
        color: "#b45309",
      };
    }

    if (paymentStatus === "PENDING") {
      return {
        background: "#fee2e2",
        color: "#dc2626",
      };
    }

    return {
      background: "#f1f5f9",
      color: "#475569",
    };
  };

  // ==========================================================
  // USER TYPE STYLE
  // ==========================================================

  const getUserTypeStyle = (type) => {
    if (type === "READY_TO_JOIN") {
      return {
        background: "#dcfce7",
        color: "#15803d",
      };
    }

    if (type === "READY_TO_VACATE") {
      return {
        background: "#fee2e2",
        color: "#dc2626",
      };
    }

    return {
      background: "#f1f5f9",
      color: "#475569",
    };
  };

  // ==========================================================
  // TITLE
  // ==========================================================

  const pageTitle =
    userType === "ALL"
      ? "All Users"
      : userType === "READY_TO_JOIN"
        ? "Ready To Join Users"
        : "Ready To Vacate Users";

  const pageSubtitle =
    userType === "ALL"
      ? "All ready to join and ready to vacate users"
      : userType === "READY_TO_JOIN"
        ? "Users who are ready to join"
        : "Users who are ready to vacate";

  // ==========================================================
  // NO DATA TEXT
  // ==========================================================

  const noDataTitle =
    userType === "ALL"
      ? "No Users"
      : userType === "READY_TO_JOIN"
        ? "No Ready To Join Users"
        : "No Ready To Vacate Users";

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* =====================================================
            HEADER
        ====================================================== */}

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{pageTitle}</h2>

            <p style={styles.subtitle}>{pageSubtitle}</p>
          </div>

          {/* ===================================================
              FILTERS
          ==================================================== */}

          <div style={styles.filters}>
            {/* USER TYPE */}

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>User Type</label>

              <select
                value={userType}
                onChange={(e) => {
                  setUserType(e.target.value);
                  setPaymentFilter("ALL");
                }}
                style={styles.select}
              >
                <option value="ALL">All Users</option>

                <option value="READY_TO_JOIN">Ready To Join</option>

                <option value="READY_TO_VACATE">Ready To Vacate</option>
              </select>
            </div>

            {/* PAYMENT STATUS */}

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Payment Status</label>

              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                style={styles.select}
              >
                <option value="ALL">All Payments</option>

                <option value="SUCCESS">Success</option>

                <option value="PAID">Paid</option>

                <option value="PARTIALLY_PAID">Partially Paid</option>

                <option value="PENDING">Pending</option>
              </select>
            </div>

            {/* COUNT */}

            <div style={styles.countBadge}>{filteredUsers.length} Users</div>
          </div>
        </div>

        {/* =====================================================
            LOADING
        ====================================================== */}

        {loading && <div style={styles.loading}>Loading users...</div>}

        {/* =====================================================
            NO DATA
        ====================================================== */}

        {!loading && filteredUsers.length === 0 && (
          <div style={styles.noData}>
            <div style={styles.noDataIcon}>👤</div>

            <h3 style={styles.noDataTitle}>{noDataTitle}</h3>

            <p>No users found for the selected payment status.</p>
          </div>
        )}

        {/* =====================================================
            TABLE
        ====================================================== */}

        {!loading && filteredUsers.length > 0 && (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              {/* =================================================
                  TABLE HEADER
              ================================================== */}

              <thead>
                <tr>
                  <th style={styles.th}>S.No</th>
                  <th style={styles.th}>User Name</th>
                  <th style={styles.th}>Mobile</th>
                  <th style={styles.th}>Floor</th>
                  <th style={styles.th}>Room</th>
                  <th style={styles.th}>Bed</th>
                  <th style={styles.th}>Joining Date</th>
                  <th style={styles.th}>Vacate Date</th>
                  <th style={styles.th}>Stay Type</th>
                  <th style={styles.th}>Payment</th>
                  <th style={styles.th}>Amount</th>
                </tr>
              </thead>

              {/* =================================================
                  TABLE BODY
              ================================================== */}

              <tbody>
                {filteredUsers.map((item, index) => (
                  <tr
                    key={item.userBookingId || item.userRegisterId || index}
                    style={styles.tr}
                  >
                    {/* S.NO */}

                    <td style={styles.td}>{index + 1}</td>

                    {/* USER */}

                    <td style={styles.td}>
                      <div style={styles.userName}>{item.fullName || "-"}</div>

                      <div style={styles.registerId}>
                        ID: {item.userRegisterId || "-"}
                      </div>
                    </td>

                    {/* USER TYPE */}

                    {/* <td style={styles.td}>
                      <span
                        style={{
                          ...styles.typeBadge,
                          ...getUserTypeStyle(item.userTypeFilter),
                        }}
                      >
                        {item.userTypeFilter === "READY_TO_JOIN"
                          ? "READY TO JOIN"
                          : "READY TO VACATE"}
                      </span>
                    </td> */}

                    {/* MOBILE */}

                    <td style={styles.td}>{item.contactNo || "-"}</td>

                    {/* FLOOR */}

                    <td style={styles.td}>{item.floorName || "-"}</td>

                    {/* ROOM */}

                    <td style={styles.td}>{item.roomName || "-"}</td>

                    {/* BED */}

                    <td style={styles.td}>
                      <span style={styles.bedBadge}>
                        {item.userBedName || "-"}
                      </span>
                    </td>

                    {/* JOINING DATE */}

                    <td style={styles.td}>
                      <span style={styles.joinDate}>
                        {formatDate(item.userBedJoiningDate)}
                      </span>
                    </td>

                    {/* VACATE DATE */}

                    <td style={styles.td}>
                      <span
                        style={
                          item.userBedVacateDate
                            ? styles.vacateDate
                            : styles.emptyDate
                        }
                      >
                        {formatDate(item.userBedVacateDate)}
                      </span>
                    </td>

                    {/* STAY TYPE */}

                    <td style={styles.td}>
                      <span style={styles.stayBadge}>
                        {item.userStayingType || "-"}
                      </span>
                    </td>

                    {/* PAYMENT */}

                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.paymentBadge,
                          ...getPaymentStatusStyle(item.paymentStatus),
                        }}
                      >
                        {item.paymentStatus || "PENDING"}
                      </span>

                      {item.paymentType && (
                        <div style={styles.paymentType}>{item.paymentType}</div>
                      )}
                    </td>

                    {/* AMOUNT */}

                    <td style={styles.td}>
                      <div style={styles.amount}>
                        ₹{" "}
                        {Number(item.userBedCost || 0).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>

                      <div style={styles.paidAmount}>
                        Paid: ₹{" "}
                        {Number(item.userPaidAmount || 0).toLocaleString(
                          "en-IN",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReadyToJoinUsers;

// ==========================================================
// STYLES
// ==========================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    padding: "25px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    boxSizing: "border-box",
  },

  container: {
    width: "100%",
    maxWidth: "1600px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: "16px",
    padding: "25px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: "25px",
    gap: "25px",
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "26px",
    fontWeight: "700",
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "14px",
  },

  filters: {
    display: "flex",
    alignItems: "flex-end",
    gap: "12px",
    flexWrap: "wrap",
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },

  filterLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
  },

  select: {
    minWidth: "180px",
    height: "40px",
    padding: "0 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "600",
    outline: "none",
    cursor: "pointer",
  },

  countBadge: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "0 18px",
    borderRadius: "20px",
    fontWeight: "700",
    fontSize: "14px",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
  },

  table: {
    width: "100%",
    minWidth: "1500px",
    borderCollapse: "collapse",
    background: "#ffffff",
  },

  th: {
    background: "#f8fafc",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "700",
    padding: "14px 12px",
    textAlign: "left",
    borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "14px 12px",
    borderBottom: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: "14px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },

  userName: {
    fontWeight: "700",
    color: "#0f172a",
    marginBottom: "3px",
  },

  registerId: {
    fontSize: "12px",
    color: "#64748b",
  },

  typeBadge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "15px",
    fontSize: "11px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  bedBadge: {
    display: "inline-block",
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "6px 10px",
    borderRadius: "7px",
    fontWeight: "700",
    fontSize: "13px",
  },

  joinDate: {
    color: "#15803d",
    fontWeight: "700",
  },

  vacateDate: {
    color: "#dc2626",
    fontWeight: "700",
  },

  emptyDate: {
    color: "#94a3b8",
  },

  stayBadge: {
    display: "inline-block",
    background: "#f1f5f9",
    color: "#475569",
    padding: "6px 10px",
    borderRadius: "7px",
    fontWeight: "600",
    fontSize: "12px",
  },

  paymentBadge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "15px",
    fontSize: "11px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  paymentType: {
    marginTop: "4px",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "600",
  },

  amount: {
    fontWeight: "700",
    color: "#0f172a",
  },

  paidAmount: {
    marginTop: "3px",
    fontSize: "12px",
    color: "#16a34a",
    fontWeight: "600",
  },

  loading: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#64748b",
    fontSize: "16px",
  },

  noData: {
    textAlign: "center",
    padding: "70px 20px",
    color: "#64748b",
  },

  noDataIcon: {
    fontSize: "45px",
    marginBottom: "10px",
  },

  noDataTitle: {
    color: "#334155",
    marginBottom: "8px",
  },

  tr: {
    transition: "background 0.2s ease",
  },
};
